/** Content `art` paths -> packed-atlas keys.
 *
 * The packer keys frames semantically (`ui.primary.attack`, `portrait.sunspire.perton`)
 * while a content def carries `art` as the source path it was packed from. Nothing
 * in the workspace maps one to the other yet; `packages/render` is expected to
 * ship the general `assetKeyFor(def)` resolver (see STATUS.md, "the second-biggest
 * gap"). This module covers only the namespaces the adventure shell draws -
 * resources, primaries, secondary skills, hero portraits and creature frames -
 * and is checked against the shipped data in the test.
 *
 * Delete it the day `packages/render` exports the real one.
 */

import type { ResourceKind } from "@game/core";

/** `UI/assets/<group>/<name>.png` -> `ui.<group>.<name>` */
export const RESOURCE_ICON: Readonly<Record<ResourceKind, string>> = Object.freeze({
  gold: "ui.resource.res_gold_ui",
  wood: "ui.resource.res_wood_ui",
  ore: "ui.resource.res_ore_ui",
  mercury: "ui.resource.res_mercury_ui",
  sulfur: "ui.resource.res_sulfur_ui",
  crystal: "ui.resource.res_crystal_ui",
  gems: "ui.resource.res_gems_ui",
});

export const PRIMARY_ICON = Object.freeze({
  attack: "ui.primary.attack",
  defense: "ui.primary.defense",
  spellPower: "ui.primary.spell_power",
  knowledge: "ui.primary.knowledge",
});

export const PRIMARY_LABEL = Object.freeze({
  attack: "Attack",
  defense: "Defense",
  spellPower: "Spell Power",
  knowledge: "Knowledge",
});

/** The order the classic prints the primaries in; not alphabetical, and it matters. */
export const PRIMARY_ORDER = Object.freeze([
  "attack", "defense", "spellPower", "knowledge",
] as const);

const stripExt = (s: string): string => {
  const dot = s.lastIndexOf(".");
  return dot > 0 ? s.slice(0, dot) : s;
};

/**
 * `PORTRAITS/assets/<faction>/<faction>_<name>.png` -> `portrait.<faction>.<name>`.
 * Verified against all 199 shipped heroes.
 */
export function portraitKey(artPath: string): string | null {
  const parts = artPath.split("/");
  const faction = parts[parts.length - 2];
  const file = parts[parts.length - 1];
  if (faction === undefined || file === undefined) return null;
  const base = stripExt(file);
  const name = base.startsWith(`${faction}_`) ? base.slice(faction.length + 1) : base;
  return `portrait.${faction}.${name}`;
}

/**
 * `UI/assets/<group>/<name>.png` -> `ui.<group>.<name>`. Covers the 28 secondary
 * skills and the mood / status icon sets.
 */
export function uiIconKey(artPath: string): string | null {
  const parts = artPath.split("/");
  const group = parts[parts.length - 2];
  const file = parts[parts.length - 1];
  if (group === undefined || file === undefined) return null;
  return `ui.${group}.${stripExt(file)}`;
}

/**
 * Creature art `<faction>/NN_<slug>.png` -> `<faction>.<slug>.front.<frame>`.
 *
 * A unit sheet is two rows of six: six front actions over the six matching rear
 * actions, and one sheet covers a creature *and* its upgrade - the file is named
 * for the pair (`06_lancer_sun_lancer.png`), so `sunspire.lancer_champion.*` is
 * the only slug either tier has. The two rows are two camera angles, not two
 * tiers. This function used to hand the upgrade the `rear` row on that
 * misreading, which drew every upgraded stack in every panel from behind.
 *
 * A UI panel - a roster socket, a garrison, a recruit socket, an army row - shows
 * a creature facing the viewer, always. The rear row exists for one caller only:
 * a battlefield stack walking away from the camera, which `packages/render`'s
 * `unitFrameKey` picks per animation step. Nothing in this package may ask for
 * it, which is why there is no parameter here to ask with.
 *
 * Six neutrals point at `obj.*` instead and have no battle sheet at all; they
 * return their `obj.` key unchanged.
 */
export function creatureFrameKey(artPath: string, frame = 0): string | null {
  if (artPath.startsWith("obj.")) return artPath;
  const parts = artPath.split("/");
  const faction = parts[parts.length - 2];
  const file = parts[parts.length - 1];
  if (faction === undefined || file === undefined) return null;
  let slug = stripExt(file);
  const us = slug.indexOf("_");
  if (us > 0 && /^\d+$/.test(slug.slice(0, us))) slug = slug.slice(us + 1);
  return `${faction}.${slug}.front.${frame}`;
}

/** `TOWNS/assets/<faction>/buildings/<faction>_<slug>.png` -> `town.<faction>.<slug>`. */
export function buildingKey(artPath: string): string | null {
  const parts = artPath.split("/");
  const file = parts[parts.length - 1];
  const faction = parts[parts.length - 3];
  if (file === undefined || faction === undefined) return null;
  const base = stripExt(file);
  const slug = base.startsWith(`${faction}_`) ? base.slice(faction.length + 1) : base;
  return `town.${faction}.${slug}`;
}
